/**
 * Unified citation data model and adapters.
 *
 * All citation sources (RAG, query evidence, review metadata, Ask responses)
 * normalize to UnifiedCitation. Consumers convert at data boundaries using
 * the adapter functions below.
 */

import type { RAGCitation, CitedEvidence, SourceReference } from './index';
import { extractCitationExcerptWithOffset, stripPageMarkers } from '../utils/extractCitationExcerpt';

// ─── Unified type ───────────────────────────────────────────────────────────────

export interface UnifiedCitation {
  /** Stable identifier (from backend or generated). */
  id: string;
  /** The source document. Always required. */
  documentId: string;
  /** Human-readable document name. Resolved from docs cache if absent. */
  documentName?: string;
  /** 1-indexed page number within the document. */
  pageNumber?: number;
  /** The quoted passage — used for text-search highlighting in PdfRenderer. */
  text: string;
  /** Broader context surrounding the quoted passage. */
  contextText?: string;
  /** Character offset start. Kept for backward compat; NOT used for highlighting. */
  startPosition?: number;
  /** Character offset end. Kept for backward compat; NOT used for highlighting. */
  endPosition?: number;
  /** RAG chunk index for passage-level provenance. */
  chunkIndex?: number;
  /** Confidence/relevance score, 0.0–1.0. Normalizes "confidence" and "relevanceScore". */
  score?: number;
  /** Whether this citation has been verified against the source document. */
  isVerified?: boolean;
  /** Source type: CLINICAL_RECORD or EXPERT_OPINION. */
  sourceType?: string;
}

// ─── DocumentViewRequest (used by DocumentViewerContext) ────────────────────────

export interface DocumentViewRequest {
  documentId: string;
  documentName: string;
  pageNumber?: number;
  /** Text to search for and highlight in the PDF viewer. */
  searchText?: string;
}

// ─── Adapter: RAGCitation → UnifiedCitation ─────────────────────────────────────

let _counter = 0;
function generateId(): string {
  return `uc-${Date.now()}-${++_counter}`;
}

export function fromRAGCitation(c: RAGCitation): UnifiedCitation {
  return {
    id: c.citationId || generateId(),
    documentId: c.documentId,
    documentName: c.documentName,
    pageNumber: c.pageNumber,
    text: c.sourceText,
    contextText: c.contextText,
    startPosition: c.startPosition,
    endPosition: c.endPosition,
    score: c.confidence,
    isVerified: c.isValid,
  };
}

// ─── Adapter: CitedEvidence → UnifiedCitation ───────────────────────────────────
// CitedEvidence (from QueryResponse) lacks documentId. Caller must supply it
// or it will be empty (navigation will fail gracefully).

export function fromCitedEvidence(
  e: CitedEvidence,
  documentId?: string,
): UnifiedCitation {
  return {
    id: generateId(),
    documentId: e.documentId || documentId || '',
    documentName: e.documentName,
    pageNumber: e.pageNumber,
    text: e.sourceText || e.quote,
    chunkIndex: e.chunkIndex,
    score: e.confidence,
  };
}

// ─── Adapter: Review Citation → UnifiedCitation ─────────────────────────────────
// The review/InlineCitations Citation type (local to that file).

export interface ReviewCitation {
  documentId: string;
  pageNumber?: number;
  chunkIndex?: number;
  text: string;
  relevanceScore: number;
}

export function fromReviewCitation(
  c: ReviewCitation,
  documentName?: string,
): UnifiedCitation {
  return {
    id: generateId(),
    documentId: c.documentId,
    documentName,
    pageNumber: c.pageNumber,
    text: c.text,
    chunkIndex: c.chunkIndex,
    score: c.relevanceScore,
  };
}

// ─── Adapter: CitationPopover Citation → UnifiedCitation ────────────────────────
// The local Citation type from CitationPopover.tsx.

export interface PopoverCitation {
  documentId: string;
  documentName: string;
  pageNumber?: number;
  excerpt?: string;
  sourceText?: string;
  startPosition?: number;
  endPosition?: number;
}

export function fromPopoverCitation(c: PopoverCitation): UnifiedCitation {
  return {
    id: generateId(),
    documentId: c.documentId,
    documentName: c.documentName,
    pageNumber: c.pageNumber,
    text: c.excerpt || c.sourceText || '',
    startPosition: c.startPosition,
    endPosition: c.endPosition,
  };
}

// ─── Adapter: Ask source → UnifiedCitation ──────────────────────────────────────

export interface AskSource {
  documentId: string;
  documentName: string;
  pageNumber?: number;
  endPageNumber?: number;
  excerpt?: string;
  sourceText?: string;
  chunkIndex?: number;
  sourceType?: string;
}

export function fromAskSource(s: AskSource): UnifiedCitation {
  return {
    id: generateId(),
    documentId: s.documentId,
    documentName: s.documentName,
    pageNumber: s.pageNumber,
    text: stripPageMarkers(s.sourceText || s.excerpt || ''),
    chunkIndex: s.chunkIndex,
    sourceType: s.sourceType,
  };
}

/**
 * Like fromAskSource, but extracts a targeted ~200-word passage from the
 * chunk using the LLM answer context. Produces much better PDF highlighting
 * because the searchText is focused on the cited passage, not the full chunk.
 */
export function fromAskSourceWithExcerpt(
  s: AskSource,
  llmAnswer: string,
  citationNumber: number,
): UnifiedCitation {
  const fullText = s.sourceText || s.excerpt || '';

  let text: string;
  let resolvedPage = s.pageNumber;

  if (s.sourceText) {
    const result = extractCitationExcerptWithOffset(llmAnswer, citationNumber, s.sourceText);
    text = result.text;

    // Resolve the actual page when the chunk spans multiple pages
    const startPage = s.pageNumber ?? 1;
    const endPage = s.endPageNumber ?? startPage;
    if (endPage > startPage && result.totalWords > 0) {
      resolvedPage = startPage + Math.round(
        (endPage - startPage) * (result.wordOffset / result.totalWords),
      );
    }
  } else {
    text = fullText;
  }

  return {
    id: generateId(),
    documentId: s.documentId,
    documentName: s.documentName,
    pageNumber: resolvedPage,
    text,
    contextText: s.sourceText || undefined,
    chunkIndex: s.chunkIndex,
    sourceType: s.sourceType,
  };
}

// ─── Adapter: SourceReference → UnifiedCitation ────────────────────────────────
// Used by ChronologyTab fallback sourceReference objects.

export function fromSourceReference(ref: SourceReference): UnifiedCitation {
  return {
    id: generateId(),
    documentId: ref.documentId,
    documentName: ref.documentName,
    pageNumber: ref.pageNumber,
    text: '',
  };
}

// ─── Adapter: Objective source → UnifiedCitation ─────────────────────────────────
// Objective analysis outputs include a top-level `sources` array referenced by
// inline [N] markers in prose text fields.

export interface ObjectiveSource {
  documentId?: string;
  documentName: string;
  pageNumber?: number;
  excerpt?: string;
}

export function fromObjectiveSource(s: ObjectiveSource): UnifiedCitation {
  return {
    id: generateId(),
    documentId: s.documentId || '',
    documentName: s.documentName,
    pageNumber: s.pageNumber,
    text: s.excerpt || '',
  };
}

// ─── Helper: fuzzy-match documentName → documentId from documents list ──────────
// LLM-generated documentName values are often descriptive (e.g. "Composite Hospital
// Admission Record") and don't match actual filenames (e.g. "original_03_complete_hospital_admission.pdf").
// This normalises both to lowercase alphanumeric tokens and checks for overlap.

function normaliseForMatch(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

export function resolveDocumentId(
  documentName: string,
  documents: { documentId: string; filename: string }[],
): string | undefined {
  if (!documentName || documents.length === 0) return undefined;

  const normName = normaliseForMatch(documentName);
  const nameTokens = normName.split(/\s+/).filter(t => t.length > 2);

  let bestMatch: string | undefined;
  let bestScore = 0;

  for (const doc of documents) {
    const normFile = normaliseForMatch(doc.filename);

    // Exact match after normalisation
    if (normFile === normName) return doc.documentId;

    // Substring containment (either direction)
    if (normFile.includes(normName) || normName.includes(normFile)) return doc.documentId;

    // Token overlap scoring
    const fileTokens = normFile.split(/\s+/).filter(t => t.length > 2);
    const overlap = nameTokens.filter(t => fileTokens.some(ft => ft.includes(t) || t.includes(ft))).length;
    const score = overlap / Math.max(nameTokens.length, 1);
    if (score > bestScore && score >= 0.3) {
      bestScore = score;
      bestMatch = doc.documentId;
    }
  }

  return bestMatch;
}

// ─── Helper: UnifiedCitation → DocumentViewRequest ──────────────────────────────
// Used by useDocumentNavigation() hook to build a view request from a citation.

export function toDocumentViewRequest(c: UnifiedCitation): DocumentViewRequest {
  const cleaned = c.text ? stripPageMarkers(c.text) : undefined;
  return {
    documentId: c.documentId,
    documentName: c.documentName || 'Document',
    pageNumber: c.pageNumber,
    searchText: cleaned || undefined,
  };
}
